<script setup>
import { computed } from 'vue';
import { getActiveLanguage, trans, transChoice } from 'laravel-vue-i18n';

/**
 * Visible author byline + publish/update dates (E-E-A-T, ТЗ §3.3).
 * Renders nothing until there is at least a date or an author to show, so it is
 * safe to drop onto every page before Phase 3 fills the data.
 */
const props = defineProps({
    author: { type: Object, default: null },       // { name, role, experience_years }
    publishedAt: { type: [String, null], default: null },
    updatedAt: { type: [String, null], default: null },
});

const locale = computed(() => (getActiveLanguage() === 'ru' ? 'ru-RU' : 'en-GB'));

const fmt = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(locale.value, { year: 'numeric', month: 'long', day: 'numeric' });
};

const publishedLabel = computed(() => fmt(props.publishedAt));
const updatedLabel = computed(() => fmt(props.updatedAt));
const hasContent = computed(() => Boolean(props.author?.name || publishedLabel.value || updatedLabel.value));
const roleLine = computed(() => {
    if (!props.author) return null;
    const parts = [];
    if (props.author.role) parts.push(props.author.role);
    if (props.author.experience_years) {
        const years = transChoice(
            '{1} :count year|[2,4] :count years|[5,*] :count years',
            props.author.experience_years,
            { count: props.author.experience_years }
        );
        parts.push(`${years} ${trans('on the platform')}`);
    }
    return parts.join(' · ') || null;
});
</script>

<template>
    <div v-if="hasContent"
         class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-2 mb-1">
        <span v-if="author?.name" class="font-medium text-slate-700 dark:text-slate-200">
            {{ author.name }}
        </span>
        <span v-if="roleLine">{{ roleLine }}</span>
        <span v-if="publishedLabel">{{ $t('Published') }}: <time :datetime="publishedAt">{{ publishedLabel }}</time></span>
        <span v-if="updatedLabel">{{ $t('Updated') }}: <time :datetime="updatedAt">{{ updatedLabel }}</time></span>
    </div>
</template>
